import { isModuleNotFoundError } from '@tanstack/router-core'
import { TRPCClientError } from '@trpc/client'
import { Component, type FunctionComponent, memo, type ReactNode, useEffect } from 'react'

import { useOpenChat, useTranslate } from '@orus.eu/pharaoh'

import { logger } from '../../lib/logger.ts'
import { getPostHogInstance } from '../../lib/posthog-integration.ts'
import { HardcodedFallbackCrashPage } from '../templates/hardcoded-fallback-crash-page.tsx'

import { GenericProblemMessage } from './generic-problem-message.tsx'

type CrashPageVariant = 'technical' | 'network'

/**
 * Transport-level failures: a tRPC response that never arrived, or a chunk the router's own
 * reload-on-module-missing could not heal. User-dependent by nature, so they are deliberately
 * kept out of the error logger (and Sentry behind it) and only recorded in PostHog, with copy
 * inviting the user to check their connection and retry.
 */
function isNetworkError(error: Error): boolean {
  return (error instanceof TRPCClientError && !error.data) || isModuleNotFoundError(error)
}

type CrashPageProps = {
  error: Error
}

/**
 * The screen displayed when the user hits an error we cannot recover from.
 *
 * Rendering this page IS the error report: it classifies and logs the error itself. Call sites
 * must not log the error, only pass it.
 */
export const CrashPage: FunctionComponent<CrashPageProps> = memo(function CrashPage({ error }) {
  const variant: CrashPageVariant = isNetworkError(error) ? 'network' : 'technical'

  useEffect(() => {
    if (variant === 'technical') {
      logger.error(error)
    }
    // Direct capture, not the tRPC-based tracking pipeline: when this page shows, our backend
    // is often the thing that's unreachable.
    getPostHogInstance()?.capture('technical_error_displayed', {
      variant,
      error_name: error.name,
      error_message: error.message,
      online: navigator.onLine,
    })
  }, [error, variant])

  return (
    <CrashPageErrorBoundary>
      <CrashPageWithContext variant={variant} />
    </CrashPageErrorBoundary>
  )
})

/**
 * Error boundary that catches any errors from CrashPageWithContext
 * and falls back to HardcodedFallbackCrashPage.
 */
class CrashPageErrorBoundary extends Component<{ children: ReactNode }, { hasError: boolean }> {
  override state = { hasError: false }

  static getDerivedStateFromError(): { hasError: boolean } {
    return { hasError: true }
  }

  override render(): ReactNode {
    if (this.state.hasError) {
      return <HardcodedFallbackCrashPage />
    }
    return this.props.children
  }
}

/**
 * The actual crash page implementation that uses context hooks.
 */
const CrashPageWithContext: FunctionComponent<{ variant: CrashPageVariant }> = memo(function CrashPageWithContext({
  variant,
}) {
  const translate = useTranslate()
  const openChat = useOpenChat()

  if (variant === 'network') {
    return (
      <GenericProblemMessage
        title={translate('problem_oops_title')}
        principalMessage={translate('problem_network_principal_message')}
        firstSubText={translate('problem_network_first_subtext')}
        buttonText={translate('problem_network_retry')}
        onButtonClick={() => window.location.reload()}
        trackingId="retry_button"
        secondaryButtonText={translate('contact_customer_support')}
        onSecondaryButtonClick={openChat}
        secondaryButtonTrackingId="contact_button"
      />
    )
  }

  return (
    <GenericProblemMessage
      title={translate('problem_oops_title')}
      principalMessage={translate('problem_principal_message')}
      firstSubText={translate('problem_first_subtext')}
      secondSubText={translate('problem_second_subtext')}
      buttonText={translate('contact_customer_support')}
      onButtonClick={openChat}
      trackingId="contact_button"
    />
  )
})
